import { TruncationMode } from '../../../../../../typography/text-ellipsis/TextEllipsis.js';
import { TEXT_ELLIPSIS } from '../constants.js';
import { getHorizontalLabelPosition } from './get-horizontal-label-position.js';
import { TRUNCATE_LABEL } from '../../../../utils/text-truncation.js';
import { charsFontWidth12pixelsSize500Map } from '../../../text-ellipsis/chars-font-width-map.js';
import { calculateWidth } from '../../../text-ellipsis/truncate-string.js';
import {
  HorizontalCoords,
  RangeLabel,
} from '../types/range-legend.internal.js';

/**
 * Calculates the starting position for a given label, in order to let the label
 * fitting the container bounds.
 */
export const calculateOptimalPlacement = (
  labelStart: number,
  labelEnd: number,
  startBound: number,
  endBound: number,
) => {
  if (labelStart < startBound) {
    const gap = startBound - labelStart;

    return labelStart + gap;
  }
  if (labelEnd > endBound) {
    const gap = endBound - labelEnd;

    return labelStart + gap;
  }

  return labelStart;
};

/**
 * Applies dynamically the ellipsis to the text until the label fit the
 * container bounds.
 */
export const calculateOptimalEllipsis = (
  text: string,
  startPosition: number,
  endPosition: number,
  truncationMode: TruncationMode,
  startBound: number,
  endBound: number,
): Required<Pick<RangeLabel, 'x'> & Pick<RangeLabel, 'text'>> => {
  let candidateStartPosX = startPosition;
  let candidateEndPosX = endPosition;
  let candidateLabel = text;
  let iteration = 0;

  do {
    candidateLabel = truncateLabel(candidateLabel, truncationMode);
    const newLabelWidth = calculateWidth(
      candidateLabel,
      charsFontWidth12pixelsSize500Map,
    );
    const { x: newCandidateStartPosX } = getHorizontalLabelPosition(
      newLabelWidth,
      startBound,
      startBound + newLabelWidth,
      'start',
    );

    candidateStartPosX = newCandidateStartPosX;
    candidateEndPosX = candidateStartPosX + newLabelWidth;
    iteration++;

    if (iteration > text.length) {
      break;
    }
  } while (candidateStartPosX < startBound || candidateEndPosX > endBound);

  return { text: candidateLabel, x: candidateStartPosX };
};

export const truncateLabel = (text: string, truncationMode: TruncationMode) => {
  const filteredText = (text ?? '').replace(TEXT_ELLIPSIS, '');

  if (filteredText.length < 2) {
    return text;
  }

  // eslint-disable-next-line @typescript-eslint/no-deprecated
  return TRUNCATE_LABEL[truncationMode](filteredText);
};

/**
 * Returns the start and ending distribution of a label.
 * @param startLabel -
 * @param endLabel -
 * @param bounds -
 */
export const getLabelsPosition = (
  startLabel: string,
  endLabel: string,
  bounds: [number, number],
): { startLabel: HorizontalCoords; endLabel: HorizontalCoords } => {
  const startLabelWidth = calculateWidth(
    startLabel,
    charsFontWidth12pixelsSize500Map,
  );
  const endLabelWidth = calculateWidth(
    endLabel,
    charsFontWidth12pixelsSize500Map,
  );

  return {
    startLabel: { start: bounds[0], end: bounds[0] + startLabelWidth },
    endLabel: { start: bounds[1] - endLabelWidth, end: bounds[1] },
  };
};
